/**
 * Cookie Settings Button Component
 *
 * A floating button that allows users to access cookie preferences
 * at any time after initial consent. Provides easy access to the
 * cookie preference center for GDPR compliance.
 */

import { Cookie } from "lucide-react";
import { useState } from "react";
import { cookieManager, type CookieConsent } from "~/lib/cookie-manager";
import { CookiePreferenceCenter } from "./CookiePreferenceCenter";

interface CookieSettingsButtonProps {
  position?: "bottom-left" | "bottom-right" | "top-left" | "top-right";
  className?: string;
}

export function CookieSettingsButton({
  position = "bottom-left",
  className = "",
}: CookieSettingsButtonProps) {
  const [showPreferences, setShowPreferences] = useState(false);

  const handlePreferencesSaved = (consent: CookieConsent) => {
    // Optional: Show a toast notification or other feedback
    console.log("Cookie preferences updated:", consent);
  };

  const getPositionClasses = () => {
    switch (position) {
      case "bottom-right":
        return "bottom-4 right-4";
      case "top-left":
        return "top-4 left-4";
      case "top-right":
        return "top-4 right-4";
      case "bottom-left":
      default:
        return "bottom-4 left-4";
    }
  };

  // Only show if consent has been set (user has interacted with cookie banner)
  if (!cookieManager.hasConsentBeenSet()) {
    return null;
  }

  return (
    <>
      {/* Floating Cookie Settings Button */}
      <button
        type="button"
        onClick={() => setShowPreferences(true)}
        className={`fixed z-40 flex aspect-square items-center gap-2 rounded-lg border border-gray-200/50 bg-white/80 px-3 py-2 text-xs font-medium text-black shadow-sm backdrop-blur-sm transition-all duration-200 ease-in-out hover:border-gray-300 hover:bg-white/90 hover:text-gray-800 hover:shadow-md ${getPositionClasses()} ${className} `}
        aria-label="Gestisci preferenze cookie"
        title="Gestisci preferenze cookie"
      >
        <Cookie className="h-4 w-4" />
      </button>

      {/* Cookie Preference Center Modal */}
      <CookiePreferenceCenter
        isOpen={showPreferences}
        onClose={() => setShowPreferences(false)}
        onSave={handlePreferencesSaved}
      />
    </>
  );
}

export default CookieSettingsButton;
